import { useEffect } from 'react';
import { Routes, Route, useParams } from 'react-router-dom';
import { usePlatformStore } from '@/lib/store';
import { TenantAdminLayout } from '@/components/layout/TenantAdminLayout';
import { Spinner } from '@/components/shared/UIComponents';
import { TenantDashboardPage } from '@/pages/tenant-admin/Dashboard';
import { BrandingPage } from '@/pages/tenant-admin/Branding';
import { FeaturesPage } from '@/pages/tenant-admin/Features';
import { B2CPlansPage } from '@/pages/tenant-admin/B2CPlans';
import { UsersPage } from '@/pages/tenant-admin/Users';
import { TripsPage } from '@/pages/tenant-admin/Trips';
import { StoriesPage } from '@/pages/tenant-admin/Stories';
import { DomainPage } from '@/pages/tenant-admin/Domain';
import { BillingPage } from '@/pages/tenant-admin/Billing';
import { SettingsPage } from '@/pages/tenant-admin/Settings';
import { DataRetentionPage } from '@/pages/tenant-admin/DataRetention';
import { TenantAppInstallsPage } from '@/pages/tenant-admin/AppInstalls';
import { FeedbackPage as TenantFeedbackPage } from '@/pages/shared/FeedbackPage';
import { AnnouncementsPage } from '@/pages/tenant-admin/Announcements';
import { BulletinsPage } from '@/pages/tenant-admin/Bulletins';
import { AffiliatesPage } from '@/pages/tenant-admin/Affiliates';
import { AffiliatePerformancePage } from '@/pages/tenant-admin/AffiliatePerformance';
import { GearPicksPage } from '@/pages/tenant-admin/GearPicks';
import { EmailPage } from '@/pages/tenant-admin/Email';
import { PlatformPlansPage } from '@/pages/shared/PlatformPlans';
import type { BrandingConfig } from '@/types';

export function TenantManagePage() {
  const { tenantId } = useParams();
  const { selectedTenant: tenant, loading, selectTenant, clearSelectedTenant } = usePlatformStore();

  useEffect(() => {
    if (tenantId) selectTenant(tenantId);
    return () => clearSelectedTenant();
  }, [tenantId, selectTenant, clearSelectedTenant]);

  if (loading || !tenant) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Spinner size="lg" />
      </div>
    );
  }

  const branding = tenant.branding as BrandingConfig | undefined;

  return (
    <TenantAdminLayout tenantName={tenant.name} brandColor={branding?.primary_color}>
      <Routes>
        <Route index element={<TenantDashboardPage tenantId={tenant.id} />} />
        <Route path="branding" element={<BrandingPage tenantId={tenant.id} />} />
        <Route path="features" element={<FeaturesPage tenantId={tenant.id} />} />
        <Route path="platform-plans" element={<PlatformPlansPage currentPlanId={tenant.plan_id} />} />
        <Route path="b2c-plans" element={<B2CPlansPage tenant={tenant} />} />
        <Route path="users" element={<UsersPage tenant={tenant} />} />
        <Route path="app-installs" element={<TenantAppInstallsPage tenantId={tenant.id} />} />
        <Route path="trips" element={<TripsPage tenant={tenant} />} />
        <Route path="stories" element={<StoriesPage tenant={tenant} />} />
        {/* Legacy /analytics path → redirect to dashboard (merged) */}
        <Route path="analytics" element={<TenantDashboardPage tenantId={tenant.id} />} />
        <Route path="announcements" element={<AnnouncementsPage tenant={tenant} />} />
        <Route path="bulletins" element={<BulletinsPage tenant={tenant} />} />
        <Route path="affiliates" element={<AffiliatesPage tenant={tenant} />} />
        <Route path="gear-picks" element={<GearPicksPage tenant={tenant} />} />
        <Route path="affiliate-performance" element={<AffiliatePerformancePage tenant={tenant} />} />
        <Route path="domain" element={<DomainPage tenant={tenant} />} />
        <Route path="billing" element={<BillingPage tenant={tenant} />} />
        <Route path="email" element={<EmailPage tenant={tenant} />} />
        <Route path="data-retention" element={<DataRetentionPage tenantId={tenant.id} />} />
        <Route path="feedback" element={<TenantFeedbackPage tenantId={tenant.id} />} />
        <Route path="settings" element={<SettingsPage tenant={tenant} />} />
      </Routes>
    </TenantAdminLayout>
  );
}
